import { useState } from 'react'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'

import PermissionsCheck from 'components/PermissionsCheck'
import { checkPermissions } from 'components/PermissionsCheck/helpers'
import usePermissions from 'hooks/usePermissions'
import { Part, Permission } from '../../../../../graphql'
import ProductForm, { ProductFormState } from '../ProductForm'

export type AddEditProductProps = {
	canMerge?: boolean,
	onMerge?: ( part: Part ) => void,
	onCancel: () => void,
	onSuccess: () => void,
	onSubmit: ( partArgs: any ) => Promise<any>,
	part?: Part,
	formData?: ProductFormState,
}

export default function AddEditProduct( { canMerge, onMerge, onCancel, onSubmit, onSuccess, part, formData }: AddEditProductProps ): JSX.Element {
	const [ isSaving, setIsSaving ] = useState( false )
	const [ formState, setFormState ] = useState<ProductFormState>( {
		partId: undefined,
		partNumber: '',
		description: '',
		provider: undefined,
		upc: undefined,
		standardPrice: undefined,
		msrp: undefined,
		isBulk: false,
		...formData,
	} )

	const { userPermissions } = usePermissions( [ Permission.CanEditPriceList ] )
	const canEditListPrice = checkPermissions( userPermissions, [ Permission.CanEditPriceList ] )

	const isValid = Boolean( formState.provider && formState.partNumber && formState.description )

	const handleSubmit = async() => {
		setIsSaving( true )
		const { provider, partNumber, description, upc, standardPrice, msrp, isBulk, productClass } = formState

		try {
			await onSubmit( {
				partId: formData?.partId,
				partNumber,
				description,
				providerId: provider?.id,
				upc,
				...( canEditListPrice && standardPrice ? { standardPrice: parseFloat( standardPrice ) } : {} ),
				msrp: msrp ? parseFloat( msrp ) : undefined,
				productClassId: isBulk ? productClass?.id : null,
			} )
			onSuccess()
		} catch {
			// The Apollo error link has already toasted the failure; stay open so the user can retry
			setIsSaving( false )
		}
	}

	return (
		<>
			<DialogContent>
				<ProductForm
					canEditListPrice={canEditListPrice}
					formState={formState}
					setFormState={setFormState}
				/>
			</DialogContent>
			<DialogActions>
				{ canMerge && onMerge && part && (
					<PermissionsCheck permissionsToCheck={[ Permission.CanMergeParts ]}>
						<Button onClick={() => onMerge( part )}>
							Merge products
						</Button>
					</PermissionsCheck>
				) }
				<Button color="inherit" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					variant="contained"
					loading={isSaving}
					disabled={!isValid}
					onClick={handleSubmit}
				>
					Submit
				</Button>
			</DialogActions>
		</>
	)
}
